import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/react/macro'
import GoogleMapsScriptProvider from '@/shared/ui/providers/GoogleMapsScriptProvider'
import PlacesAutocomplete from '@/components/ui/comboboxes/PlacesAutocomplete'
import { PatientLocationData } from '../lib/patient-location.types'
import { LocationQuickPick, OPTION_LABELS } from './LocationQuickPick'
import { usePatientLocation } from '../model/usePatientLocation'

type PatientLocationModalProps = {
  open: boolean
  onClose: () => void
  consultationId: string
  initialLocation: PatientLocationData | null
}

export function PatientLocationModal({ open, onClose, consultationId, initialLocation }: PatientLocationModalProps) {
  const { i18n } = useLingui()
  const { selected, selectedPlace, isOther, isSaving, handleSelect, handlePlaceSelect, handleSave } = usePatientLocation(
    consultationId,
    initialLocation,
    onClose
  )

  const description = selected ? (isOther ? (selectedPlace?.description ?? null) : i18n._(OPTION_LABELS[selected])) : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 4, pt: 4, pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.dark', letterSpacing: '-0.02em' }}>
          <Trans>Your location</Trans>
        </Typography>
        <IconButton onClick={onClose} sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, pb: 0 }}>
        <LocationQuickPick selected={selected} onSelect={handleSelect} />

        {isOther && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                <Trans>Specify the address</Trans>
              </Typography>
              <Box sx={{ flex: 1, height: 2, bgcolor: 'grey.100', borderRadius: 1 }} />
            </Box>
            <GoogleMapsScriptProvider>
              <PlacesAutocomplete defaultValue={selectedPlace?.description} onSelect={handlePlaceSelect} />
            </GoogleMapsScriptProvider>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 4, pt: 3, pb: 3, flexDirection: 'column', gap: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={() => description && handleSave(description)}
          disabled={!description || isSaving}
          endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
          sx={{
            borderRadius: 3,
            py: 1.5,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 12px 24px -4px rgba(63,100,116,0.25)',
            '&:hover': {
              boxShadow: '0 12px 24px -4px rgba(63,100,116,0.4)',
            },
          }}
        >
          {isSaving ? <Trans>Saving...</Trans> : <Trans>Confirm my location</Trans>}
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', opacity: 0.7 }}>
          <Trans>Please indicate the location from which you are participating in this teleconsultation.</Trans>
        </Typography>
      </DialogActions>
    </Dialog>
  )
}
